// src/features/records/components/RecordDetail/components/RecordComments.js
import React, { useMemo, useState, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  MessageCircle,
  Reply as ReplyIcon,
  MoreHorizontal,
  Trash2,
  ChevronDown,
  ChevronUp,
  ArrowUp,
  X,
  AlertCircle
} from 'lucide-react';
import useComments from '../hooks/useComments';
import useCommentDeepLinkFocus from '../hooks/useCommentDeepLinkFocus';
import useUser from '../../../../../hooks/useUser';
import useUserProfiles from '../../../hooks/useUserProfiles';
import { useToast } from '../../../../../hooks/useToast';
import { addRootComment, addReply, deleteComment } from '../../../services/interactions/comments';
import useRecordCommentCount, { invalidateRecordCommentCount } from '../../../hooks/useRecordCommentCount';
import mapRecordsErrorToKey from '../../../utils/recordsErrorMapper';
import { Avatar, AvatarSkeleton } from '../../../../../components/avatars';
import { sanitizeText } from '../../../../../utils/sanitize';
import { formatDate, formatTimeAgoCompact } from '../../../../../utils/dateUtils';
import styles from './RecordComments.module.css';

const MAX_COMMENT_LENGTH = 5000;
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;
const toDateValue = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') {
    return value.toDate();
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatCompactTime = (date, { locale, t, now } = {}) => {
  return formatTimeAgoCompact(date, { locale, t, now });
};

const formatFullDate = (date, { locale, t } = {}) => {
  if (!date) return '';
  return formatDate(date, {
    locale,
    t,
    formatOptions: {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    },
  });
};

const getReplyCountFromComment = (comment) => {
  if (!comment) return null;
  if (Array.isArray(comment.replies)) {
    return comment.replies.length;
  }

  const candidates = [
    comment.replyCount,
    comment.repliesCount,
    comment.reply_count,
    comment.replies_count
  ];

  for (const value of candidates) {
    if (value == null) continue;
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return null;
};

const RecordComments = ({ recordId, canComment, initialFocusTarget }) => {
  const { t } = useTranslation();
  const {
    rootComments,
    repliesByRootId,
    replyPresenceByRootId,
    loadingRoots,
    loadingMoreRoots,
    loadingRepliesByRootId,
    error,
    hasMoreRoots,
    loadMoreRoots,
    loadReplies,
    stopRepliesListener,
    markCommentDeleted,
    ensureRootCommentLoaded
  } = useComments(recordId);
  const { count: commentCount } = useRecordCommentCount(recordId);
  const { userId } = useUser();
  const toast = useToast();
  const lastErrorRef = useRef(null);

  const [composerText, setComposerText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [expandedRoots, setExpandedRoots] = useState({});
  const [pending, setPending] = useState({
    submittingRoot: false,
    submittingReply: false,
    deletingCommentId: null
  });
  const [uiError, setUiError] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  useEffect(() => {
    setComposerText('');
    setReplyingTo(null);
    setReplyText('');
    setExpandedRoots({});
    setPending({
      submittingRoot: false,
      submittingReply: false,
      deletingCommentId: null
    });
    setUiError(null);
    setConfirmDeleteId(null);
  }, [recordId]);

  useEffect(() => {
    if (!error) {
      lastErrorRef.current = null;
      return;
    }

    const errorKey = mapRecordsErrorToKey(error, {
      fallbackKey: 'records.comments.errors.load_failed'
    });
    if (lastErrorRef.current === errorKey) {
      return;
    }

    lastErrorRef.current = errorKey;
    toast.error(t(errorKey));
  }, [error, toast, t]);

  useEffect(() => {
    if (!replyingTo) return;
    const root = rootComments.find((comment) => comment?.id === replyingTo.rootId);
    if (!root || root.status === 'deleted') {
      setReplyingTo(null);
      setReplyText('');
      setUiError((prev) => (prev?.scope === 'reply' ? null : prev));
    }
  }, [replyingTo, rootComments]);

  const visibleRootComments = useMemo(
    () => rootComments.filter((comment) => comment?.status !== 'deleted'),
    [rootComments]
  );

  const authorIds = useMemo(() => {
    const ids = new Set();
    visibleRootComments.forEach((comment) => {
      if (comment?.authorId) {
        ids.add(comment.authorId);
      }
      const replies = (repliesByRootId[comment?.id] || [])
        .filter((reply) => reply?.status !== 'deleted');
      replies.forEach((reply) => {
        if (reply?.authorId) {
          ids.add(reply.authorId);
        }
      });
    });
    return Array.from(ids);
  }, [visibleRootComments, repliesByRootId]);

  const { profiles } = useUserProfiles(authorIds);

  const getProfile = useCallback((authorId) => {
    if (!authorId) return null;
    return profiles?.[authorId] || null;
  }, [profiles]);

  const getDisplayName = useCallback((authorId) => {
    const profile = getProfile(authorId);
    const raw = profile?.displayName || profile?.username || '';
    const sanitized = sanitizeText(raw).trim();
    return sanitized || t('records.comments.author_unknown', 'Unknown user');
  }, [getProfile, t]);

  const { highlightedCommentId } = useCommentDeepLinkFocus({
    recordId,
    initialFocusTarget,
    rootComments,
    repliesByRootId,
    loadingRoots,
    loadingRepliesByRootId,
    expandedRoots,
    setExpandedRoots,
    loadReplies,
    ensureRootCommentLoaded,
    toast
  });

  const handleSubmitRoot = useCallback(async (text) => {
    if (!canComment || pending.submittingRoot) {
      return;
    }

    const trimmed = text.trim();
    if (!trimmed) {
      setUiError({
        message: t('records.comments.errors.root_required', 'Comment text is required.'),
        scope: 'root'
      });
      return;
    }
    if (trimmed.length > MAX_COMMENT_LENGTH) {
      setUiError({
        message: t('records.comments.errors.root_too_long', {
          maxLength: MAX_COMMENT_LENGTH,
          defaultValue: 'Comment is too long.'
        }),
        scope: 'root'
      });
      return;
    }

    try {
      setUiError(null);
      setPending((prev) => ({ ...prev, submittingRoot: true }));
      await addRootComment(recordId, trimmed);
      invalidateRecordCommentCount(recordId);
      setComposerText('');
    } catch (err) {
      console.error('Failed to add comment:', err);
      const errorKey = mapRecordsErrorToKey(err, {
        fallbackKey: 'records.comments.errors.add_failed'
      });
      toast.error(t(errorKey));
    } finally {
      setPending((prev) => ({ ...prev, submittingRoot: false }));
    }
  }, [canComment, pending.submittingRoot, recordId, toast, t]);

  const handleToggleReplies = useCallback(async (rootId) => {
    const willExpand = !expandedRoots[rootId];
    setExpandedRoots((prev) => ({ ...prev, [rootId]: willExpand }));

    if (willExpand) {
      await loadReplies(rootId);
    } else {
      stopRepliesListener(rootId);
      if (replyingTo?.rootId === rootId) {
        setReplyingTo(null);
        setReplyText('');
        setUiError((prev) => (prev?.scope === 'reply' ? null : prev));
      }
    }
  }, [expandedRoots, loadReplies, stopRepliesListener, replyingTo]);

  const handleReplyClick = useCallback(async (rootId, authorName) => {
    if (!canComment) {
      return;
    }

    setReplyingTo({ rootId, authorName });
    setReplyText('');
    setUiError((prev) => (prev?.scope === 'reply' ? null : prev));

    if (!expandedRoots[rootId]) {
      setExpandedRoots((prev) => ({ ...prev, [rootId]: true }));
      await loadReplies(rootId);
    }
  }, [canComment, expandedRoots, loadReplies]);

  const handleSubmitReply = useCallback(async (text) => {
    if (!canComment || pending.submittingReply || !replyingTo) {
      return;
    }

    const trimmed = text.trim();
    if (!trimmed) {
      setUiError({
        message: t('records.comments.errors.reply_required', 'Reply text is required.'),
        scope: 'reply'
      });
      return;
    }
    if (trimmed.length > MAX_COMMENT_LENGTH) {
      setUiError({
        message: t('records.comments.errors.reply_too_long', {
          maxLength: MAX_COMMENT_LENGTH,
          defaultValue: 'Reply is too long.'
        }),
        scope: 'reply'
      });
      return;
    }

    try {
      setUiError(null);
      setPending((prev) => ({ ...prev, submittingReply: true }));
      await addReply(recordId, replyingTo.rootId, trimmed);
      invalidateRecordCommentCount(recordId);
      setReplyText('');
      setReplyingTo(null);
    } catch (err) {
      console.error('Failed to add reply:', err);
      const errorKey = mapRecordsErrorToKey(err, {
        fallbackKey: 'records.comments.errors.reply_failed'
      });
      toast.error(t(errorKey));
    } finally {
      setPending((prev) => ({ ...prev, submittingReply: false }));
    }
  }, [canComment, pending.submittingReply, recordId, replyingTo, toast, t]);

  const handleCancelReply = useCallback(() => {
    setReplyingTo(null);
    setReplyText('');
    setUiError((prev) => (prev?.scope === 'reply' ? null : prev));
  }, []);

  const handleDeleteRequest = useCallback((commentId) => {
    setConfirmDeleteId(commentId);
  }, []);

  const handleCancelDelete = useCallback(() => {
    if (pending.deletingCommentId) {
      return;
    }
    setConfirmDeleteId(null);
  }, [pending.deletingCommentId]);

  const handleConfirmDelete = useCallback(async (commentId) => {
    if (!commentId) {
      return;
    }

    try {
      setPending((prev) => ({ ...prev, deletingCommentId: commentId }));
      await deleteComment(recordId, commentId);
      markCommentDeleted(commentId);
      invalidateRecordCommentCount(recordId);
      setConfirmDeleteId(null);
    } catch (err) {
      console.error('Failed to delete comment:', err);
      const errorKey = mapRecordsErrorToKey(err, {
        fallbackKey: 'records.comments.errors.delete_failed'
      });
      toast.error(t(errorKey));
    } finally {
      setPending((prev) => ({ ...prev, deletingCommentId: null }));
    }
  }, [recordId, toast, markCommentDeleted, t]);

  const handleComposerChange = useCallback((value) => {
    setComposerText(value);
    if (uiError?.scope === 'root') {
      setUiError(null);
    }
  }, [uiError]);

  const handleReplyComposerChange = useCallback((value) => {
    setReplyText(value);
    if (uiError?.scope === 'reply') {
      setUiError(null);
    }
  }, [uiError]);

  const hasComments = visibleRootComments.length > 0 || hasMoreRoots;

  return (
    <div
      className={styles.commentsSection}
      id={`record-comments-${recordId}`}
      data-record-id={recordId}
    >
      <CommentsHeader count={commentCount} />

      {canComment && (
        <div className={styles.composerWrapper}>
          <CommentComposer
            value={composerText}
            onChange={handleComposerChange}
            onSubmit={() => handleSubmitRoot(composerText)}
            placeholder={t('records.comments.composer.root_placeholder', 'Share your thoughts...')}
            submitLabel={pending.submittingRoot
              ? t('records.comments.composer.posting', 'Posting...')
              : t('records.comments.composer.post', 'Post')}
            isSubmitting={pending.submittingRoot}
            maxLength={MAX_COMMENT_LENGTH}
            error={uiError?.scope === 'root' ? uiError.message : null}
            dataAttribute="root"
          />
        </div>
      )}

      {loadingRoots ? (
        <CommentsSkeleton />
      ) : !hasComments ? (
        <EmptyState canComment={canComment} />
      ) : (
        <div className={styles.commentList}>
          {rootComments.length === 0 && (
            <EmptyState canComment={canComment} />
          )}

          {visibleRootComments.map((comment) => {
            const replies = (repliesByRootId[comment.id] || [])
              .filter((reply) => reply?.status !== 'deleted');
            const hasLoadedReplies = Object.prototype.hasOwnProperty.call(repliesByRootId, comment.id);
            const replyPresence = replyPresenceByRootId[comment.id];
            const isExpanded = Boolean(expandedRoots[comment.id]);
            const isRootAuthor = Boolean(userId && comment.authorId === userId);
            const canReplyToRoot = canComment;

            return (
              <CommentThread
                key={comment.id}
                rootComment={comment}
                replies={replies}
                hasLoadedReplies={hasLoadedReplies}
                replyPresence={replyPresence}
                isExpanded={isExpanded}
                isLoadingReplies={loadingRepliesByRootId[comment.id]}
                onToggleReplies={() => handleToggleReplies(comment.id)}
                canReply={canReplyToRoot}
                canComment={canComment}
                onReply={handleReplyClick}
                isReplying={replyingTo?.rootId === comment.id}
                replyingToName={replyingTo?.authorName || getDisplayName(comment.authorId)}
                replyText={replyText}
                onReplyTextChange={handleReplyComposerChange}
                onSubmitReply={() => handleSubmitReply(replyText)}
                onCancelReply={handleCancelReply}
                getProfile={getProfile}
                getDisplayName={getDisplayName}
                userId={userId}
                isRootAuthor={isRootAuthor}
                confirmDeleteId={confirmDeleteId}
                onRequestDelete={handleDeleteRequest}
                onConfirmDelete={handleConfirmDelete}
                onCancelDelete={handleCancelDelete}
                deletingCommentId={pending.deletingCommentId}
                isSubmittingReply={pending.submittingReply}
                uiError={uiError?.scope === 'reply' ? uiError.message : null}
                highlightedCommentId={highlightedCommentId}
              />
            );
          })}

          {hasMoreRoots && (
            <button
              className={styles.loadMoreButton}
              onClick={loadMoreRoots}
              disabled={loadingMoreRoots}
              type="button"
            >
              {loadingMoreRoots
                ? t('records.comments.loading_more', 'Loading...')
                : t('records.comments.load_more', 'Load more comments')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const CommentsHeader = ({ count }) => {
  const { t } = useTranslation();

  return (
    <div className={styles.commentsHeader}>
      <h3 className={styles.commentsTitle}>
        <MessageCircle size={20} />
        {t('records.comments.title', 'Comments')}
        {count > 0 && <span className={styles.commentsCount}>({count})</span>}
      </h3>
    </div>
  );
};

const CommentComposer = ({
  value,
  onChange,
  onSubmit,
  onCancel,
  placeholder,
  submitLabel,
  isSubmitting,
  maxLength,
  error,
  autoFocus = false,
  replyingTo,
  isReply = false,
  dataAttribute
}) => {
  const { t } = useTranslation();
  const textareaRef = useRef(null);

  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [autoFocus]);

  useIsomorphicLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
  }, [value]);

  const trimmedValue = value.trim();
  const canSubmit = trimmedValue.length > 0 && !isSubmitting;
  const showSendButton = trimmedValue.length > 0 || isSubmitting;
  const sendButtonLabel = isSubmitting
    ? (isReply
      ? t('records.comments.composer.posting_reply_aria', 'Posting reply')
      : t('records.comments.composer.posting_comment_aria', 'Posting comment'))
    : submitLabel;

  const handleSubmit = (event) => {
    event?.preventDefault();
    if (!canSubmit) return;
    onSubmit();
  };

  const handleKeyDown = (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div
      className={styles.composer}
    >
      {replyingTo && (
        <div className={styles.composerHeader}>
          <ReplyIcon size={14} />
          <span>
            {t('records.comments.composer.replying_to', {
              name: replyingTo,
              defaultValue: 'Replying to {{name}}'
            })}
          </span>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className={styles.composerHeaderClose}
              aria-label={t('records.comments.composer.cancel_reply', 'Cancel reply')}
              title={t('records.comments.composer.cancel_reply', 'Cancel reply')}
            >
              <X size={14} />
            </button>
          )}
        </div>
      )}

      <form className={styles.composerBody} onSubmit={handleSubmit}>
        <div className={styles.composerInputRow}>
          <textarea
            ref={textareaRef}
            className={styles.composerTextarea}
            placeholder={placeholder}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            maxLength={maxLength}
            disabled={isSubmitting}
            data-reply={isReply}
            data-comment-composer={dataAttribute === 'root' ? 'root' : undefined}
            aria-label={placeholder}
          />
          <button
            type="submit"
            className={[
              styles.composerSendButton,
              showSendButton ? styles.composerSendButtonVisible : '',
              isSubmitting ? styles.composerSendButtonLoading : ''
            ]
              .filter(Boolean)
              .join(' ')}
            disabled={!canSubmit}
            aria-label={sendButtonLabel}
            title={sendButtonLabel}
            aria-hidden={!showSendButton}
            tabIndex={showSendButton ? 0 : -1}
          >
            {isSubmitting ? (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
                className={styles.composerSendSpinner}
                width={18}
                height={18}
              >
                <circle cx="12" cy="12" r="9" />
              </svg>
            ) : (
              <ArrowUp size={18} />
            )}
          </button>
        </div>

        {error && (
          <div className={styles.composerError}>
            <AlertCircle size={14} />
            <span>{error}</span>
          </div>
        )}

      </form>
    </div>
  );
};

const CommentThread = ({
  rootComment,
  replies,
  hasLoadedReplies,
  replyPresence,
  isExpanded,
  isLoadingReplies,
  onToggleReplies,
  canReply,
  canComment,
  onReply,
  isReplying,
  replyingToName,
  replyText,
  onReplyTextChange,
  onSubmitReply,
  onCancelReply,
  getProfile,
  getDisplayName,
  userId,
  isRootAuthor,
  confirmDeleteId,
  onRequestDelete,
  onConfirmDelete,
  onCancelDelete,
  deletingCommentId,
  isSubmittingReply,
  uiError,
  highlightedCommentId
}) => {
  const { t } = useTranslation();
  const knownReplyCount = getReplyCountFromComment(rootComment);
  const hasKnownReplyCount = Number.isFinite(knownReplyCount);
  const replyPresenceKnown = typeof replyPresence === 'boolean';
  const hasActiveReplies = hasLoadedReplies
    ? replies.length > 0
    : replyPresenceKnown
      ? replyPresence
      : false;
  const replyCount = hasLoadedReplies
    ? replies.length
    : hasKnownReplyCount
      ? knownReplyCount
      : null;
  const hasReplies = hasActiveReplies || (!hasLoadedReplies && !replyPresenceKnown
    ? (hasKnownReplyCount ? knownReplyCount > 0 : false)
    : false);
  const replyCountLabel = Number.isFinite(replyCount) && replyCount > 0
    ? t('records.comments.replies.count', {
      count: replyCount,
      defaultValue_one: '{{count}} reply',
      defaultValue_other: '{{count}} replies'
    })
    : t('records.comments.replies.count_unknown', 'replies');
  const replyLabel = isExpanded
    ? t('records.comments.replies.hide', {
      label: replyCountLabel,
      defaultValue: 'Hide {{label}}'
    })
    : t('records.comments.replies.show', {
      label: replyCountLabel,
      defaultValue: 'Show {{label}}'
    });
  const showThreadLine = isExpanded && (hasReplies || replies.length > 0 || isReplying);
  const showReplySection = isExpanded || hasReplies;
  const showReplyToggle = hasReplies;
  const isRootOwn = isRootAuthor;

  return (
    <div
      className={styles.commentThread}
      data-own={isRootOwn}
      style={{
        '--comment-thread-line-bottom': isReplying ? '88px' : '12px'
      }}
    >
      {showThreadLine && <div className={styles.threadLine} />}

      <CommentItem
        comment={rootComment}
        profile={getProfile(rootComment.authorId)}
        authorName={getDisplayName(rootComment.authorId)}
        isAuthor={isRootAuthor}
        isReply={false}
        canReply={canReply}
        canComment={canComment}
        onReply={onReply}
        rootId={rootComment.id}
        rootHasReplies={hasReplies}
        isHighlighted={highlightedCommentId === rootComment.id}
        confirmDeleteId={confirmDeleteId}
        onRequestDelete={onRequestDelete}
        onConfirmDelete={onConfirmDelete}
        onCancelDelete={onCancelDelete}
        deletingCommentId={deletingCommentId}
      />

      {showReplySection && (
        <div className={styles.replySection}>
          {showReplyToggle && (
            <button
              className={styles.replyToggle}
              type="button"
              onClick={onToggleReplies}
              aria-expanded={isExpanded}
            >
              {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {replyLabel}
            </button>
          )}

          {isExpanded && (
            <>
              {isLoadingReplies && (
                <div className={styles.replyMeta}>
                  {t('records.comments.replies.loading', 'Loading replies...')}
                </div>
              )}

              {!isLoadingReplies && replies.length === 0 && (
                <div className={styles.replyMeta}>
                  {t('records.comments.replies.empty', 'No replies yet')}
                </div>
              )}

              {replies.length > 0 && (
                <div className={styles.replyList}>
                  {replies.map((reply) => (
                    <CommentItem
                      key={reply.id}
                      comment={reply}
                      profile={getProfile(reply.authorId)}
                      authorName={getDisplayName(reply.authorId)}
                      isAuthor={Boolean(userId && reply.authorId === userId)}
                      isReply
                      canReply={canReply}
                      canComment={canComment}
                      onReply={onReply}
                      rootId={rootComment.id}
                      isHighlighted={highlightedCommentId === reply.id}
                      confirmDeleteId={confirmDeleteId}
                      onRequestDelete={onRequestDelete}
                      onConfirmDelete={onConfirmDelete}
                      onCancelDelete={onCancelDelete}
                      deletingCommentId={deletingCommentId}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {isReplying && (
        <div className={styles.replyComposer}>
          <CommentComposer
            value={replyText}
            onChange={onReplyTextChange}
            onSubmit={onSubmitReply}
            onCancel={onCancelReply}
            placeholder={t('records.comments.composer.reply_placeholder', {
              name: replyingToName,
              defaultValue: 'Reply to {{name}}...'
            })}
            replyingTo={replyingToName}
            submitLabel={isSubmittingReply
              ? t('records.comments.composer.posting', 'Posting...')
              : t('records.comments.actions.reply', 'Reply')}
            isSubmitting={isSubmittingReply}
            maxLength={MAX_COMMENT_LENGTH}
            error={uiError}
            autoFocus
            isReply
          />
        </div>
      )}
    </div>
  );
};

const CommentItem = ({
  comment,
  profile,
  authorName,
  isAuthor,
  isReply,
  canReply,
  canComment,
  onReply,
  rootId,
  rootHasReplies,
  isHighlighted,
  confirmDeleteId,
  onRequestDelete,
  onConfirmDelete,
  onCancelDelete,
  deletingCommentId
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const isConfirmingDelete = confirmDeleteId === comment.id;
  const createdAt = toDateValue(comment?.createdAt);
  const timeLabel = createdAt
    ? formatCompactTime(createdAt, { locale, t })
    : t('records.time.relative.just_now', 'Just now');
  const fullTime = createdAt ? formatFullDate(createdAt, { locale, t }) : '';
  const replyTargetId = rootId || comment.id;

  useEffect(() => {
    if (!menuOpen) return undefined;

    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen]);

  if (comment?.status === 'deleted') {
    return null;
  }

  if (isConfirmingDelete) {
    const deleteMessage = isReply
      ? t('records.comments.delete_confirm.reply', 'Delete this reply?')
      : rootHasReplies
        ? t(
          'records.comments.delete_confirm.comment_with_replies',
          'Delete this comment and all replies?'
        )
        : t('records.comments.delete_confirm.comment', 'Delete this comment?');
    return (
      <div
        className={styles.inlineDeleteWrapper}
        style={isReply ? { marginLeft: '44px' } : undefined}
      >
        <InlineDeleteConfirm
          onCancel={onCancelDelete}
          onConfirm={() => onConfirmDelete(comment.id)}
          isDeleting={deletingCommentId === comment.id}
          message={deleteMessage}
        />
      </div>
    );
  }

  return (
    <article
      className={[
        styles.commentItem,
        isAuthor && !isReply ? styles.commentItemOwn : '',
        isHighlighted ? styles.commentHighlight : ''
      ]
        .filter(Boolean)
        .join(' ')}
      id={`record-comment-${comment.id}`}
      data-comment-id={comment.id}
    >
      <Avatar
        name={authorName}
        src={profile?.profilePictureUrl}
        size={isReply ? 'sm' : 'md'}
        className={styles.commentAvatar}
        alt={authorName
          ? t('records.comments.avatar_alt', {
            name: authorName,
            defaultValue: "{{name}}'s avatar"
          })
          : t('records.comments.avatar_fallback_alt', 'User avatar')}
      />

      <div className={styles.commentBody}>
        <div className={styles.commentHeader}>
          <span className={styles.commentAuthor}>{authorName}</span>
          {isAuthor && <span className={styles.commentBadge}>{t('records.comments.you_badge', 'You')}</span>}
          <time
            className={styles.commentTime}
            title={fullTime || undefined}
            dateTime={createdAt ? createdAt.toISOString() : undefined}
          >
            {timeLabel}
          </time>
        </div>

        <p className={styles.commentText}>{sanitizeText(comment.text)}</p>

        {(canReply || (isAuthor && canComment)) && (
          <div className={styles.commentActions}>
            {canReply && (
              <button
                type="button"
                className={styles.iconButton}
                onClick={() => onReply(replyTargetId, authorName)}
                aria-label={t('records.comments.actions.reply', 'Reply')}
                title={t('records.comments.actions.reply', 'Reply')}
              >
                <ReplyIcon size={15} />
              </button>
            )}

            {isAuthor && canComment && (
              <div className={styles.commentMenu}>
                <button
                  ref={buttonRef}
                  type="button"
                  className={`${styles.iconButton} ${styles.commentMenuButton}`}
                  aria-label={t('records.comments.actions.more', 'More options')}
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  title={t('records.comments.actions.more', 'More options')}
                  data-open={menuOpen}
                  onClick={() => setMenuOpen((prev) => !prev)}
                >
                  <MoreHorizontal size={16} />
                </button>

                {menuOpen && (
                  <div
                    ref={menuRef}
                    className={styles.commentMenuPopover}
                    role="menu"
                  >
                    <button
                      type="button"
                      role="menuitem"
                      className={`${styles.commentMenuItem} ${styles.commentMenuItemDanger}`}
                      onClick={() => {
                        setMenuOpen(false);
                        onRequestDelete(comment.id);
                      }}
                    >
                      <Trash2 size={16} />
                      {t('records.comments.actions.delete', 'Delete')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </article>
  );
};

const InlineDeleteConfirm = ({ onConfirm, onCancel, isDeleting, message }) => {
  const { t } = useTranslation();

  return (
    <div className={styles.inlineDelete}>
      <AlertCircle size={16} />
      <span className={styles.inlineDeleteText}>{message}</span>
      <button
        type="button"
        className={`${styles.actionButton} ${styles.actionButtonGhost}`}
        onClick={onCancel}
        disabled={isDeleting}
      >
        {t('common.cancel', 'Cancel')}
      </button>
      <button
        type="button"
        className={`${styles.actionButton} ${styles.actionButtonDanger}`}
        onClick={onConfirm}
        disabled={isDeleting}
      >
        {isDeleting ? t('common.deleting', 'Deleting') : t('records.comments.actions.delete', 'Delete')}
      </button>
    </div>
  );
};

const EmptyState = ({ canComment }) => {
  const { t } = useTranslation();

  return (
    <div className={styles.emptyState}>
      <div className={styles.emptyIcon}>
        <MessageCircle size={28} />
      </div>
      <h4 className={styles.emptyTitle}>{t('records.comments.empty.title', 'No comments yet')}</h4>
      <p className={styles.emptyText}>
        {canComment
          ? t(
            'records.comments.empty.can_comment_description',
            'Be the first to share your thoughts on this record.'
          )
          : t(
            'records.comments.empty.read_only_description',
            'Comments will appear here once added.'
          )}
      </p>
    </div>
  );
};

const CommentsSkeleton = () => (
  <div className={styles.commentsSkeleton}>
    <CommentSkeleton />
    <div className={styles.commentSkeletonThread}>
      <CommentSkeleton />
      <div className={styles.commentSkeletonReplies}>
        <CommentSkeleton isReply />
      </div>
    </div>
    <CommentSkeleton />
  </div>
);

const CommentSkeleton = ({ isReply = false }) => (
  <div className={`${styles.commentSkeleton} ${isReply ? styles.commentSkeletonReply : ''}`}
  >
    <AvatarSkeleton size={isReply ? 'sm' : 'md'} className={styles.commentSkeletonAvatar} />
    <div className={styles.commentSkeletonBody}>
      <div className={styles.commentSkeletonMeta}>
        <div className={styles.skeletonLine} data-size="name" />
        <div className={styles.skeletonLine} data-size="time" />
      </div>
      <div className={styles.skeletonLine} data-size="line" />
      <div className={styles.skeletonLine} data-size="lineShort" />
    </div>
  </div>
);

CommentSkeleton.propTypes = {
  isReply: PropTypes.bool
};

InlineDeleteConfirm.propTypes = {
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isDeleting: PropTypes.bool,
  message: PropTypes.string.isRequired
};

CommentItem.propTypes = {
  comment: PropTypes.shape({
    id: PropTypes.string.isRequired,
    authorId: PropTypes.string,
    text: PropTypes.string,
    createdAt: PropTypes.any,
    status: PropTypes.string
  }).isRequired,
  profile: PropTypes.shape({
    displayName: PropTypes.string,
    username: PropTypes.string,
    profilePictureUrl: PropTypes.string
  }),
  authorName: PropTypes.string.isRequired,
  isAuthor: PropTypes.bool,
  isReply: PropTypes.bool,
  canReply: PropTypes.bool,
  canComment: PropTypes.bool,
  onReply: PropTypes.func,
  rootId: PropTypes.string,
  rootHasReplies: PropTypes.bool,
  isHighlighted: PropTypes.bool,
  confirmDeleteId: PropTypes.string,
  onRequestDelete: PropTypes.func,
  onConfirmDelete: PropTypes.func,
  onCancelDelete: PropTypes.func,
  deletingCommentId: PropTypes.string
};

CommentThread.propTypes = {
  rootComment: PropTypes.shape({
    id: PropTypes.string.isRequired,
    authorId: PropTypes.string,
    text: PropTypes.string,
    createdAt: PropTypes.any,
    status: PropTypes.string
  }).isRequired,
  replies: PropTypes.arrayOf(PropTypes.object),
  hasLoadedReplies: PropTypes.bool,
  replyPresence: PropTypes.bool,
  isExpanded: PropTypes.bool,
  isLoadingReplies: PropTypes.bool,
  onToggleReplies: PropTypes.func.isRequired,
  canReply: PropTypes.bool,
  canComment: PropTypes.bool,
  onReply: PropTypes.func,
  isReplying: PropTypes.bool,
  replyingToName: PropTypes.string,
  replyText: PropTypes.string,
  onReplyTextChange: PropTypes.func,
  onSubmitReply: PropTypes.func,
  onCancelReply: PropTypes.func,
  getProfile: PropTypes.func.isRequired,
  getDisplayName: PropTypes.func.isRequired,
  userId: PropTypes.string,
  isRootAuthor: PropTypes.bool,
  confirmDeleteId: PropTypes.string,
  onRequestDelete: PropTypes.func,
  onConfirmDelete: PropTypes.func,
  onCancelDelete: PropTypes.func,
  deletingCommentId: PropTypes.string,
  isSubmittingReply: PropTypes.bool,
  uiError: PropTypes.string,
  highlightedCommentId: PropTypes.string
};

CommentComposer.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  placeholder: PropTypes.string,
  submitLabel: PropTypes.string,
  isSubmitting: PropTypes.bool,
  maxLength: PropTypes.number.isRequired,
  error: PropTypes.string,
  autoFocus: PropTypes.bool,
  replyingTo: PropTypes.string,
  isReply: PropTypes.bool,
  dataAttribute: PropTypes.string
};

CommentsHeader.propTypes = {
  count: PropTypes.number
};

EmptyState.propTypes = {
  canComment: PropTypes.bool
};

RecordComments.propTypes = {
  recordId: PropTypes.string.isRequired,
  canComment: PropTypes.bool,
  initialFocusTarget: PropTypes.shape({
    threadRootId: PropTypes.string,
    commentId: PropTypes.string
  })
};

export default RecordComments;
